import React, { useState } from 'react';
import { PlugIcon, CheckCircleIcon, XCircleIcon, SettingsIcon, RefreshCwIcon, WifiIcon, AlertCircleIcon, XIcon, KeyIcon, GlobeIcon, ZapIcon, CarIcon } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
export function IntegrationsPage() {
  const {
    integrations
  } = useAppContext();
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<string | null>(null);
  const bookingEngines = integrations.filter(i => i.type === 'booking_engine');
  const groundTransport = integrations.filter(i => i.type === 'ground_transport');
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected':
        return 'bg-emerald-100 text-emerald-700';
      case 'disconnected':
        return 'bg-slate-100 text-slate-600';
      case 'configuring':
        return 'bg-amber-100 text-amber-700';
      default:
        return 'bg-slate-100 text-slate-600';
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected':
        return <CheckCircleIcon className="h-4 w-4 text-emerald-600" data-id="element-1505" />;
      case 'disconnected':
        return <XCircleIcon className="h-4 w-4 text-slate-400" data-id="element-1506" />;
      case 'configuring':
        return <SettingsIcon className="h-4 w-4 text-amber-600 animate-spin" data-id="element-1507" />;
      default:
        return <XCircleIcon className="h-4 w-4 text-slate-400" data-id="element-1508" />;
    }
  };
  const openConfig = (id: string) => {
    setSelectedIntegration(id);
    setShowConfigModal(true);
  };
  return <div className="space-y-6" data-id="element-1509">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1510">
        <div data-id="element-1511">
          <h2 className="text-2xl font-bold text-navy-900" data-id="element-1512">Integrations</h2>
          <p className="text-slate-500" data-id="element-1513">
            Connect booking engines and transport providers to streamline
            operations.
          </p>
        </div>
      </div>

      {/* Integration Health Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1514">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-id="element-1515">
          <div className="flex items-center gap-2 mb-2" data-id="element-1516">
            <div className="h-8 w-8 rounded-full bg-emerald-50 flex items-center justify-center" data-id="element-1517">
              <CheckCircleIcon className="h-4 w-4 text-emerald-600" data-id="element-1518" />
            </div>
            <span className="text-xs font-semibold text-slate-500 uppercase" data-id="element-1519">
              Connected
            </span>
          </div>
          <p className="text-2xl font-bold text-navy-900" data-id="element-1520">
            {integrations.filter(i => i.status === 'connected').length}
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-id="element-1521">
          <div className="flex items-center gap-2 mb-2" data-id="element-1522">
            <div className="h-8 w-8 rounded-full bg-amber-50 flex items-center justify-center" data-id="element-1523">
              <SettingsIcon className="h-4 w-4 text-amber-600" data-id="element-1524" />
            </div>
            <span className="text-xs font-semibold text-slate-500 uppercase" data-id="element-1525">
              Configuring
            </span>
          </div>
          <p className="text-2xl font-bold text-navy-900" data-id="element-1526">
            {integrations.filter(i => i.status === 'configuring').length}
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-id="element-1527">
          <div className="flex items-center gap-2 mb-2" data-id="element-1528">
            <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center" data-id="element-1529">
              <XCircleIcon className="h-4 w-4 text-slate-400" data-id="element-1530" />
            </div>
            <span className="text-xs font-semibold text-slate-500 uppercase" data-id="element-1531">
              Disconnected
            </span>
          </div>
          <p className="text-2xl font-bold text-navy-900" data-id="element-1532">
            {integrations.filter(i => i.status === 'disconnected').length}
          </p>
        </div>
      </div>

      {/* Booking Engines */}
      <div data-id="element-1533">
        <div className="flex items-center gap-2 mb-4" data-id="element-1534">
          <GlobeIcon className="h-5 w-5 text-navy-700" data-id="element-1535" />
          <h3 className="text-lg font-bold text-navy-900" data-id="element-1536">Booking Engines</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4" data-id="element-1537">
          TMP handles workflow, policies, approvals, and reporting. Booking
          engines handle inventory, pricing, and ticket issuance.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1538">
          {bookingEngines.map(integration => <div key={integration.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-1539">
              <div className="flex items-start justify-between mb-4" data-id="element-1540">
                <div className="flex items-center gap-3" data-id="element-1541">
                  <div className="h-12 w-12 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center text-2xl" data-id="element-1542">
                    {integration.logo}
                  </div>
                  <div data-id="element-1543">
                    <h4 className="font-bold text-slate-900" data-id="element-1544">
                      {integration.name}
                    </h4>
                    <div className="flex items-center gap-1.5 mt-0.5" data-id="element-1545">
                      {getStatusIcon(integration.status)}
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${getStatusColor(integration.status)}`} data-id="element-1546">
                        {integration.status.charAt(0).toUpperCase() + integration.status.slice(1)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs text-slate-500 mb-4" data-id="element-1547">
                <span className="flex items-center gap-1" data-id="element-1548">
                  <RefreshCwIcon className="h-3 w-3" data-id="element-1549" /> Last sync:{' '}
                  {integration.lastSync}
                </span>
              </div>
              <div className="flex gap-2" data-id="element-1550">
                <button onClick={() => openConfig(integration.id)} className="flex-1 flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-1551">
                  <SettingsIcon className="h-3.5 w-3.5" data-id="element-1552" /> Configure
                </button>
                {integration.status === 'connected' ? <button className="flex items-center justify-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-100 transition-colors" data-id="element-1553">
                    <ZapIcon className="h-3.5 w-3.5" data-id="element-1554" /> Test
                  </button> : <button className="flex items-center justify-center gap-1.5 rounded-lg bg-navy-900 px-3 py-2 text-xs font-medium text-white hover:bg-navy-700 transition-colors" data-id="element-1555">
                    <PlugIcon className="h-3.5 w-3.5" data-id="element-1556" /> Connect
                  </button>}
              </div>
            </div>)}
        </div>
      </div>

      {/* Ground Transport */}
      <div data-id="element-1557">
        <div className="flex items-center gap-2 mb-4" data-id="element-1558">
          <CarIcon className="h-5 w-5 text-navy-700" data-id="element-1559" />
          <h3 className="text-lg font-bold text-navy-900" data-id="element-1560">Ground Transport</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4" data-id="element-1561">
          Airport pickups, transfer scheduling, and cost tracking integrated
          into traveler itineraries.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1562">
          {groundTransport.map(integration => <div key={integration.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-1563">
              <div className="flex items-center gap-3 mb-3" data-id="element-1564">
                <div className="h-10 w-10 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-xl" data-id="element-1565">
                  {integration.logo}
                </div>
                <div data-id="element-1566">
                  <h4 className="font-bold text-sm text-slate-900" data-id="element-1567">
                    {integration.name}
                  </h4>
                  <div className="flex items-center gap-1 mt-0.5" data-id="element-1568">
                    {getStatusIcon(integration.status)}
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${getStatusColor(integration.status)}`} data-id="element-1569">
                      {integration.status.charAt(0).toUpperCase() + integration.status.slice(1)}
                    </span>
                  </div>
                </div>
              </div>
              <p className="text-xs text-slate-500 mb-3" data-id="element-1570">
                Last sync: {integration.lastSync}
              </p>
              <button onClick={() => openConfig(integration.id)} className="w-full flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-1571">
                <SettingsIcon className="h-3.5 w-3.5" data-id="element-1572" /> Configure
              </button>
            </div>)}
        </div>
      </div>

      {/* Configuration Modal */}
      {showConfigModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" data-id="element-1573">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl" data-id="element-1574">
            <div className="flex items-center justify-between mb-6" data-id="element-1575">
              <h3 className="text-lg font-bold text-slate-900" data-id="element-1576">
                Integration Configuration
              </h3>
              <button onClick={() => setShowConfigModal(false)} className="rounded-full p-1 hover:bg-slate-100" data-id="element-1577">
                <XIcon className="h-5 w-5 text-slate-500" data-id="element-1578" />
              </button>
            </div>
            <div className="space-y-4" data-id="element-1579">
              <div data-id="element-1580">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1581">
                  API Key
                </label>
                <div className="relative" data-id="element-1582">
                  <KeyIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-1583" />
                  <input type="password" className="w-full rounded-lg border border-slate-300 p-2.5 pl-10 text-sm font-mono" placeholder="sk-..." data-id="element-1584" />
                </div>
              </div>
              <div data-id="element-1585">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1586">
                  API Endpoint
                </label>
                <input type="url" className="w-full rounded-lg border border-slate-300 p-2.5 text-sm font-mono" placeholder="https://api.example.com/v1" data-id="element-1587" />
              </div>
              <div data-id="element-1588">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1589">
                  Webhook URL
                </label>
                <input type="url" className="w-full rounded-lg border border-slate-300 p-2.5 text-sm font-mono" placeholder="https://your-domain.com/webhook" data-id="element-1590" />
              </div>
              <div data-id="element-1591">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1592">
                  Mapping Rules
                </label>
                <textarea rows={3} className="w-full rounded-lg border border-slate-300 p-2.5 text-sm font-mono" placeholder='{"field_mapping": {...}}' data-id="element-1593" />
              </div>
              <div className="flex gap-3 pt-2" data-id="element-1594">
                <button onClick={() => setShowConfigModal(false)} className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50" data-id="element-1595">
                  Cancel
                </button>
                <button className="flex items-center justify-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2.5 text-sm font-medium text-emerald-700 hover:bg-emerald-100" data-id="element-1596">
                  <ZapIcon className="h-4 w-4" data-id="element-1597" /> Test Connection
                </button>
                <button onClick={() => setShowConfigModal(false)} className="rounded-lg bg-navy-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-700" data-id="element-1598">
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}